import { assign, createMachine } from 'xstate';
import { Transaction } from '../types';
import {
  isTransactionPending,
  setTransactionToError,
  setTransactionToSuccess,
  getStoredTransactions,
  storeTransactions,
} from './utilities';

type Context = { transactions: Transaction[] };
type Event =
  | { type: 'ADD'; transaction: Transaction }
  | { type: 'SUCCESS'; hash: string }
  | { type: 'ERROR'; hash: string }
  | { type: 'REMOVE'; hash: string }
  | { type: 'CLEAR' };

export const transactionMachine = createMachine(
  {
    id: 'transactions',
    schema: {
      context: { transactions: [] } as Context,
      events: {} as Event,
    },
    context: {
      transactions: getStoredTransactions(),
    },
    initial: 'default',
    states: {
      default: {},
      pending: {},
      success: {
        after: {
          6000: [{ target: 'pending', cond: 'hasPending' }, { target: 'default' }],
        },
        on: {
          ADD: {},
        },
      },
      error: {
        after: {
          6000: [{ target: 'pending', cond: 'hasPending' }, { target: 'default' }],
        },
        on: {
          ADD: {},
        },
      },
    },
    on: {
      ADD: {
        target: 'pending',
        actions: ['addTransaction', 'storeTransactions'],
      },
      SUCCESS: { target: 'success', actions: ['setTransactionToSuccess', 'storeTransactions'] },
      ERROR: { target: 'error', actions: ['setTransactionToError', 'storeTransactions'] },
      CLEAR: { target: 'default', actions: ['clearTransactions', 'storeTransactions'] },
      REMOVE: { target: 'default', actions: ['removeTransaction'] },
    },
  },
  {
    actions: {
      addTransaction: assign({
        transactions: (context, event) => {
          if (event.type !== 'ADD') {
            return context.transactions;
          }

          return [...context.transactions, event.transaction];
        },
      }),
      removeTransaction: assign({
        transactions: (context, event) => {
          if (event.type !== 'REMOVE') return context.transactions;

          return context.transactions.filter((transaction) => transaction.tx.hash !== event.hash);
        },
      }),
      setTransactionToSuccess: assign({
        transactions: (context, event) => {
          if (event.type !== 'SUCCESS') {
            return context.transactions;
          }

          return setTransactionToSuccess(event.hash, context.transactions);
        },
      }),
      setTransactionToError: assign({
        transactions: (context, event) => {
          if (event.type !== 'ERROR') {
            return context.transactions;
          }

          return setTransactionToError(event.hash, context.transactions);
        },
      }),
      clearTransactions: assign({
        transactions: (context) => [],
      }),
      storeTransactions: ({ transactions }) => {
        storeTransactions(transactions);
      },
    },
    guards: {
      hasPending: ({ transactions }) => transactions.some(isTransactionPending),
    },
  }
);
